import React, { useState, useRef, useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { FiFileText, FiClock, FiCheckSquare, FiAlertCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import ActionButton from "../../../Common/ActionButton";
import SearchFilter from "../../../Common/SearchFilter";
import ColumnVisibilityControl from "../../../Common/ColumnVisibilityControl";
import StatusChip from "../../../Common/StatusChip";
import DateRangeFilter from "../../../../Common/DateFilter";
import ReportCard from "../../../Common/ReportCard";
import useReportData from "../../useReportData";

const PRToPOConversionPipelineReport = () => {
  const gridRef = useRef(null);
  const [data] = useReportData("PrConversion");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [urgencyFilter, setUrgencyFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [columnVisibility, setColumnVisibility] = useState({});

  // Summary KPI metrics
  const kpis = useMemo(() => {
    const convertedCount = data.filter(
      (r) => r.convertedPoNumber && !r.convertedPoNumber.includes("Unassigned"),
    ).length;
    const pendingConversionCount = data.length - convertedCount;
    const itemsWithTurnaround = data.filter((r) => r.turnaroundDays !== null);
    const avgTurnaround = itemsWithTurnaround.length
      ? (
          itemsWithTurnaround.reduce((s, r) => s + r.turnaroundDays, 0) / itemsWithTurnaround.length
        ).toFixed(1)
      : 0;

    return {
      totalPRs: data.length,
      convertedCount,
      pendingConversionCount,
      avgTurnaround,
    };
  }, [data]);

  const getApprovalChipType = (status) => {
    if (status === "Approved") {
      return "success";
    }
    if (status === "Pending Approval") {
      return "warning";
    }
    if (status === "Rejected") {
      return "error";
    }
    return "default";
  };

  const getUrgencyBadge = (urgency) => {
    if (urgency === "Urgent") {
      return { bg: "#fee2e2", color: "#b91c1c" };
    }
    if (urgency === "High") {
      return { bg: "#fef3c7", color: "#b45309" };
    }
    return { bg: "#f1f5f9", color: "#475569" };
  };

  // Column definitions
  const columnDefs = useMemo(() => {
    return [
      {
        headerName: "Sr",
        colId: "sr",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        minWidth: 60,
        maxWidth: 70,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
      },
      {
        headerName: "Requisition Number",
        field: "prNumber",
        colId: "prNumber",
        flex: 1.2,
        minWidth: 140,
        cellRenderer: (p) => (
          <span
            style={{
              fontWeight: 700,
              fontFamily: "monospace",
              color: "var(--color-primary, #0b6462)",
            }}
          >
            {p.value}
          </span>
        ),
      },
      {
        headerName: "PR Date & Site",
        field: "prDate",
        colId: "prDate",
        flex: 1.5,
        minWidth: 170,
        cellRenderer: (p) => (
          <div>
            <div style={{ fontWeight: 600, color: "#1e293b" }}>{p.value}</div>
            <small style={{ color: "#64748b" }}>{p.data?.project}</small>
          </div>
        ),
      },
      {
        headerName: "Requested By",
        field: "requestedBy",
        colId: "requestedBy",
        flex: 1.6,
        minWidth: 180,
        cellRenderer: (p) => <span style={{ fontWeight: 500, color: "#334155" }}>{p.value}</span>,
      },
      {
        headerName: "Required By & Urgency",
        field: "requiredByDate",
        colId: "requiredByDate",
        flex: 1.4,
        minWidth: 160,
        cellRenderer: (p) => {
          const badge = getUrgencyBadge(p.data?.urgency);
          return (
            <div className="d-flex align-items-center" style={{ gap: "6px" }}>
              <span style={{ fontWeight: 600, color: "#334155", fontSize: "12px" }}>{p.value}</span>
              <span
                style={{
                  background: badge.bg,
                  color: badge.color,
                  padding: "1px 6px",
                  borderRadius: "10px",
                  fontSize: "10px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                {p.data?.urgency}
              </span>
            </div>
          );
        },
      },
      {
        headerName: "Material Description, Requested Qty & UOM",
        field: "materialDesc",
        colId: "materialDesc",
        flex: 2.2,
        minWidth: 240,
        cellRenderer: (p) => (
          <div>
            <div style={{ fontWeight: 600, color: "#0f172a" }}>{p.value}</div>
            <small style={{ color: "#0b6462", fontWeight: 600 }}>
              Qty: {p.data?.requestedQty ? Number(p.data.requestedQty).toLocaleString() : 0}{" "}
              {p.data?.uom}
            </small>
          </div>
        ),
      },
      {
        headerName: "Approved Status",
        field: "approvalStatus",
        colId: "approvalStatus",
        flex: 1.3,
        minWidth: 150,
        cellRenderer: (p) => <StatusChip status={p.value} type={getApprovalChipType(p.value)} />,
      },
      {
        headerName: "Converted PO Number",
        field: "convertedPoNumber",
        colId: "convertedPoNumber",
        flex: 1.5,
        minWidth: 180,
        cellRenderer: (p) => {
          const isPending = !p.value || p.value.includes("Unassigned");
          return isPending ? (
            <span
              style={{
                fontStyle: "italic",
                color: "#94a3b8",
                fontSize: "12px",
              }}
            >
              Unassigned / Pending Order
            </span>
          ) : (
            <span
              style={{
                background: "#f0fdf4",
                color: "#15803d",
                padding: "3px 8px",
                borderRadius: "4px",
                fontFamily: "monospace",
                fontWeight: 700,
                fontSize: "12px",
                border: "1px solid #bbf7d0",
              }}
            >
              {p.value}
            </span>
          );
        },
      },
      {
        headerName: "Turnaround Time",
        field: "turnaroundDays",
        colId: "turnaroundDays",
        flex: 1.2,
        minWidth: 130,
        cellRenderer: (p) => {
          if (p.value === null || p.value === undefined) {
            return <span style={{ color: "#94a3b8" }}>—</span>;
          }
          return (
            <span
              style={{
                fontWeight: 700,
                color: p.value <= 3 ? "#16a34a" : p.value <= 5 ? "#d97706" : "#dc2626",
              }}
            >
              {p.value} {p.value === 1 ? "Day" : "Days"}
            </span>
          );
        },
      },
    ];
  }, []);

  // Filter logic
  const filteredData = useMemo(() => {
    return data.filter((row) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches =
          (row.prNumber && row.prNumber.toLowerCase().includes(q)) ||
          (row.project && row.project.toLowerCase().includes(q)) ||
          (row.requestedBy && row.requestedBy.toLowerCase().includes(q)) ||
          (row.materialDesc && row.materialDesc.toLowerCase().includes(q)) ||
          (row.convertedPoNumber && row.convertedPoNumber.toLowerCase().includes(q));
        if (!matches) {
          return false;
        }
      }

      if (statusFilter !== "all" && row.approvalStatus !== statusFilter) {
        return false;
      }

      if (urgencyFilter !== "all" && row.urgency !== urgencyFilter) {
        return false;
      }

      if (dateRange.from && row.prDate && row.prDate < dateRange.from) {
        return false;
      }
      if (dateRange.to && row.prDate && row.prDate > dateRange.to) {
        return false;
      }

      return true;
    });
  }, [data, searchTerm, statusFilter, urgencyFilter, dateRange]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: false,
      resizable: true,
    }),
    [],
  );

  const toggleColumn = useCallback((colId) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [colId]: !prev[colId] };
      if (gridRef.current?.api) {
        gridRef.current.api.setColumnsVisible([colId], next[colId]);
      }
      return next;
    });
  }, []);

  const handleExportCSV = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `PR_to_PO_Conversion_Pipeline_${new Date().toISOString().slice(0, 10)}.csv`,
      });
      toast.success("PR to PO conversion report exported to CSV");
    }
  };

  return (
    <div className="pt-2 app_font">
      {/* ── Summary Cards ── */}
      <div className="row g-2 mb-1 mt-1">
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="TOTAL REQUISITIONS"
            value={`${kpis.totalPRs} Demands`}
            subtitle="Raised by site teams"
            icon={FiFileText}
            color="#0b6462"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="CONVERTED TO PO"
            value={`${kpis.convertedCount} Issued`}
            subtitle="Successfully ordered"
            icon={FiCheckSquare}
            color="#16a34a"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="PENDING PO CONVERSION"
            value={`${kpis.pendingConversionCount} Demands`}
            subtitle="Requires procurement action"
            icon={FiAlertCircle}
            color="#d97706"
          />
        </div>
        <div className="col-lg-3 col-md-6 col-sm-6">
          <ReportCard
            title="AVG CONVERSION SPEED"
            value={`${kpis.avgTurnaround} Days`}
            subtitle="Turnaround time from PR to PO"
            icon={FiClock}
            color="#0284c7"
          />
        </div>
      </div>

      {/* ── Filter Toolbar (Search First, then Date, then other filters) ── */}
      <div className="row app_font">
        <div className="col-12 pt-1 p-0">
          <div
            className="d-flex flex-wrap align-items-center justify-content-end pt-2 pb-2 app_font w-100"
            style={{ gap: "12px", width: "100%" }}
          >
            <div
              className="d-flex align-items-center"
              style={{
                minHeight: "30px",
                maxWidth: "100%",
                marginRight: "auto",
                padding: "4px 12px",
                color: "var(--btn-comp-text, #0b6462)",
                fontSize: "var(--font-md)",
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}
              title="PR to PO Conversion"
            >
              PR to PO Conversion
            </div>
            {/* Search Filter FIRST */}
            <SearchFilter
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search PR, item, requester..."
            />

            {/* Date Filter SECOND */}
            <div className="d-flex align-items-center" style={{ maxWidth: "260px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <DateRangeFilter
                  initialFrom={dateRange.from}
                  initialTo={dateRange.to}
                  onChange={(val) => setDateRange(val)}
                />
                <label>Date</label>
              </div>
            </div>

            {/* Approval Status Filter THIRD */}
            <div className="d-flex align-items-center" style={{ maxWidth: "200px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Approval Status</option>
                  <option value="Approved">Approved</option>
                  <option value="Pending Approval">Pending Approval</option>
                  <option value="Draft">Draft</option>
                  <option value="Rejected">Rejected</option>
                </select>
                <label>Approval Status</label>
              </div>
            </div>

            {/* Urgency Filter */}
            <div className="d-flex align-items-center" style={{ maxWidth: "180px", flexGrow: 1 }}>
              <div className="floating-field mt-1" style={{ width: "100%" }}>
                <select
                  className="input_styling"
                  value={urgencyFilter}
                  onChange={(e) => setUrgencyFilter(e.target.value)}
                >
                  <option value="all">All Urgency Levels</option>
                  <option value="Urgent">Urgent</option>
                  <option value="High">High</option>
                  <option value="Normal">Normal</option>
                </select>
                <label>Urgency Level</label>
              </div>
            </div>

            <ColumnVisibilityControl
              gridApi={gridRef.current?.api}
              columnVisibility={columnVisibility}
              toggleColumn={toggleColumn}
            />
          </div>
        </div>
      </div>

      {/* ── AG Grid Table ── */}
      <div className="row">
        <div className="col-12 pr-0 pl-0">
          <div
            className="ag-theme-alpine vouchers_table_height3 mt-1"
            style={{ width: "100%", position: "relative" }}
          >
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={15}
              animateRows={true}
            />
            {/* Table Footer Actions */}
            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "15px",
                display: "flex",
                gap: "10px",
                zIndex: 1,
              }}
            >
              <ActionButton
                text="Download CSV"
                isPrint={true}
                newBtn="true"
                onClick={handleExportCSV}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PRToPOConversionPipelineReport;
